import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaDownload,
  FaLock,
  FaChevronDown,
  FaFileLines,
} from "react-icons/fa6";
import { toast } from "react-toastify";
import Header from "../components/Header";
import api from "../utils/axiosConfig";
import { downloadBlobFile } from "../utils/downloadBlob";
import CourseVideoPlayer from "../components/CourseVideoPlayer";
import { CourseDetailView } from "../utils/entities/course";
import { resolveResumeLesson, isLessonDone } from "../utils/courseNavigation";
import {
  CoursePageShell,
  CourseBreadcrumb,
  CourseNavBar,
  CourseLoading,
  CourseLessonStepper,
  MaterialTypeIcon,
  formatFileSize,
} from "../components/courses/CourseUi";

const CourseLesson: React.FC = () => {
  const { id, lessonId: lessonIdParam } = useParams<{
    id: string;
    lessonId: string;
  }>();
  const courseId = Number(id);
  const lessonId = Number(lessonIdParam);
  const isValidCourseId = Number.isFinite(courseId) && courseId > 0;
  const navigate = useNavigate();

  const [course, setCourse] = useState<CourseDetailView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [materialsOpen, setMaterialsOpen] = useState(false);
  const [downloadingMaterialId, setDownloadingMaterialId] = useState<
    number | null
  >(null);

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      const silent = opts?.silent === true;
      try {
        const { data } = await api.get<CourseDetailView>(
          `agent/courses/${courseId}`
        );
        setCourse(data);
        if (!silent) setError(null);
        return data;
      } catch {
        if (!silent) {
          setError("Não foi possível carregar o curso.");
        } else {
          toast.error("Não foi possível atualizar o progresso da aula.");
        }
        return null;
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [courseId]
  );

  useEffect(() => {
    if (isValidCourseId) {
      setLoading(true);
      load();
    } else {
      setLoading(false);
      setError("Curso inválido.");
    }
  }, [isValidCourseId, lessonId, load]);

  useEffect(() => {
    setMaterialsOpen(false);
  }, [lessonId]);

  const lessonIndex = useMemo(() => {
    if (!course) return -1;
    return course.lessons.findIndex((l) => l.id === lessonId);
  }, [course, lessonId]);

  const lesson = lessonIndex >= 0 ? course!.lessons[lessonIndex] : null;
  const nextLesson =
    course && lessonIndex >= 0 && lessonIndex < course.lessons.length - 1
      ? course.lessons[lessonIndex + 1]
      : null;
  const prevLesson =
    course && lessonIndex > 0 ? course.lessons[lessonIndex - 1] : null;

  useEffect(() => {
    if (!course || loading) return;
    const target = course.lessons.find((l) => l.id === lessonId);
    if (!target) {
      const resume = resolveResumeLesson(course.lessons);
      if (resume) {
        navigate(`/courses/${courseId}/lessons/${resume.id}`, {
          replace: true,
        });
      } else {
        navigate(`/courses/${courseId}`, { replace: true });
      }
      return;
    }
    if (!target.unlocked) {
      const resume = resolveResumeLesson(course.lessons);
      if (resume && resume.id !== lessonId) {
        navigate(`/courses/${courseId}/lessons/${resume.id}`, {
          replace: true,
        });
      }
    }
  }, [course, lessonId, courseId, navigate, loading]);

  const handleVideoCompleted = useCallback(() => {
    // Atualiza estado local a partir do progresso já confirmado pelo player,
    // para o footer não ficar preso se o refetch silencioso falhar.
    setCourse((prev) => {
      if (!prev) return prev;
      const idx = prev.lessons.findIndex((l) => l.id === lessonId);
      if (idx < 0) return prev;
      const current = prev.lessons[idx];
      const hasExam = !!current.hasExam;
      const lessons = prev.lessons.map((l, i) => {
        if (i !== idx) return l;
        return {
          ...l,
          videoCompleted: true,
          // Sem prova: concluir a aula e liberar a próxima.
          // Com prova: só libera a prova; a aula conclui após aprovação.
          completed: hasExam ? l.completed : true,
          examUnlocked: hasExam && !l.examLocked ? true : l.examUnlocked,
          video: l.video ? { ...l.video, completed: true } : l.video,
        };
      });
      if (!hasExam && idx < lessons.length - 1 && !lessons[idx + 1].unlocked) {
        lessons[idx + 1] = { ...lessons[idx + 1], unlocked: true };
      }
      return {
        ...prev,
        lessons,
        allLessonsCompleted: lessons.every((l) => l.completed),
      };
    });
    void load({ silent: true });
  }, [load, lessonId]);

  const handleDownloadMaterial = useCallback(
    async (materialId: number, fileName: string) => {
      setDownloadingMaterialId(materialId);
      try {
        await downloadBlobFile(
          `agent/courses/${courseId}/lessons/${lessonId}/materials/${materialId}/download`,
          fileName
        );
      } catch {
        toast.error("Erro ao baixar material.");
      } finally {
        setDownloadingMaterialId(null);
      }
    },
    [courseId, lessonId]
  );

  const goNext = () => {
    if (!lesson) return;
    if (lesson.examUnlocked) {
      navigate(`/courses/${courseId}/lessons/${lesson.id}/exam`);
      return;
    }
    if (nextLesson?.unlocked) {
      navigate(`/courses/${courseId}/lessons/${nextLesson.id}`);
    }
  };

  if (loading) {
    return (
      <>
        <Header />
        <CourseLoading />
      </>
    );
  }

  if (error || !course || !lesson) {
    return (
      <>
        <Header />
        <CoursePageShell>
          <CourseBreadcrumb items={[{ label: "Cursos", to: "/courses" }]} />
          <p className="text-red-600">{error ?? "Aula não encontrada."}</p>
        </CoursePageShell>
      </>
    );
  }

  const isDone = isLessonDone(lesson);
  const examPending = !!lesson.examUnlocked;
  const canGoNext = examPending || (isDone && !!nextLesson?.unlocked);
  const nextLabel = examPending
    ? "Ir para a prova da aula"
    : nextLesson && isDone && nextLesson.unlocked
    ? "Próxima aula"
    : !isDone
    ? lesson.hasExam
      ? "Conclua o vídeo e a prova para continuar"
      : "Conclua a aula para continuar"
    : nextLesson
    ? "Atualizando…"
    : lesson.examLocked
    ? "Prova da aula bloqueada"
    : "Fim do curso";

  return (
    <>
      <Header />
      <CoursePageShell withStickyNav>
        <CourseBreadcrumb
          items={[
            { label: "Cursos", to: "/courses" },
            { label: course.title, to: `/courses/${courseId}` },
          ]}
        />

        <CourseLessonStepper
          lessons={course.lessons.map((l) => ({
            id: l.id,
            unlocked: l.unlocked,
            completed: isLessonDone(l),
          }))}
          currentLessonId={lesson.id}
          onSelect={(id) => navigate(`/courses/${courseId}/lessons/${id}`)}
        />

        <div className="mb-5">
          <h1 className="text-xl font-bold text-slate-900 tracking-tight">
            {lesson.title}
          </h1>
        </div>

        {!lesson.unlocked ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-center text-slate-500 shadow-sm">
            <FaLock className="mx-auto mb-2 text-xl text-slate-400" />
            <p className="text-sm font-medium">
              Conclua a aula anterior para liberar esta
            </p>
          </div>
        ) : (
          <>
            {lesson.video && lesson.video.signedUrl ? (
              <div className="mb-5">
                <CourseVideoPlayer
                  courseId={courseId}
                  lessonId={lesson.id}
                  video={lesson.video}
                  onCompleted={handleVideoCompleted}
                />
              </div>
            ) : (
              <p className="text-sm text-red-600 mb-5 p-3.5 bg-red-50 rounded-xl border border-red-100">
                Vídeo indisponível nesta aula. Atualize a página ou entre em
                contato com o suporte.
              </p>
            )}

            {lesson.examUnlocked && (
              <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5">
                <p className="text-sm font-semibold text-amber-900 tracking-tight">
                  Prova da aula liberada
                </p>
                <p className="text-sm text-amber-800 mt-1">
                  Assista ao vídeo concluído. Agora faça a prova para concluir
                  esta aula
                  {lesson.maxAttempts != null
                    ? ` (até ${lesson.maxAttempts} tentativas; nota mínima do curso: ${course.passingScore}%).`
                    : "."}
                </p>
              </div>
            )}

            {lesson.examLocked && !lesson.completed && (
              <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3.5">
                <p className="text-sm font-semibold text-red-800 tracking-tight">
                  Prova da aula bloqueada
                </p>
                <p className="text-sm text-red-700 mt-1">
                  Tentativas esgotadas ({lesson.maxAttempts ?? 3}). Esta aula
                  não pode ser concluída e as próximas permanecem bloqueadas.
                </p>
              </div>
            )}

            {lesson.description && (
              <div className="mb-4 rounded-xl border border-slate-200 bg-white px-4 py-3.5 shadow-sm">
                <h2 className="text-sm font-semibold text-slate-900 tracking-tight mb-1.5">
                  Sobre esta aula
                </h2>
                <p className="text-sm text-slate-600 leading-relaxed">
                  {lesson.description}
                </p>
              </div>
            )}

            {lesson.materials.length > 0 && (
              <div className="mb-4 rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                <button
                  type="button"
                  onClick={() => setMaterialsOpen((o) => !o)}
                  className="flex w-full items-center justify-between gap-2 px-4 py-3.5 text-left hover:bg-slate-50/80 transition-all focus:outline-none focus:ring-2 focus:ring-inset focus:ring-preppoBlue/30"
                  aria-expanded={materialsOpen}
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-slate-900 tracking-tight">
                    <FaFileLines className="text-slate-400 text-sm" />
                    Materiais de apoio ({lesson.materials.length})
                  </span>
                  <FaChevronDown
                    className={`text-slate-400 text-xs transition-transform duration-200 ease-out ${
                      materialsOpen ? "rotate-180" : "rotate-0"
                    }`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-[220ms] ease-out ${
                    materialsOpen
                      ? "max-h-[800px] opacity-100 border-t border-slate-100"
                      : "max-h-0 opacity-0"
                  }`}
                >
                  <ul className="divide-y divide-slate-100">
                    {lesson.materials.map((material) => {
                      const sizeLabel = formatFileSize(material.fileSizeBytes);
                      return (
                        <li key={material.id}>
                          <button
                            type="button"
                            onClick={() =>
                              handleDownloadMaterial(
                                material.id,
                                material.fileName || material.title
                              )
                            }
                            disabled={downloadingMaterialId === material.id}
                            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 disabled:opacity-60 transition-colors"
                          >
                            <MaterialTypeIcon
                              fileName={material.fileName}
                              mimeType={material.mimeType}
                              size="sm"
                              className="!h-7 !w-7 rounded-full"
                            />
                            <span className="flex-1 min-w-0 text-sm font-medium text-slate-900 tracking-tight truncate">
                              {material.title}
                              {sizeLabel ? (
                                <span className="ml-1.5 font-normal text-slate-400">
                                  {sizeLabel}
                                </span>
                              ) : null}
                            </span>
                            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-preppoBlue shrink-0">
                              <FaDownload />
                              {downloadingMaterialId === material.id
                                ? "Baixando..."
                                : "Baixar"}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            )}
          </>
        )}

        <CourseNavBar
          onPrev={
            prevLesson
              ? () =>
                  navigate(`/courses/${courseId}/lessons/${prevLesson.id}`)
              : undefined
          }
          prevDisabled={!prevLesson}
          prevDisabledTooltip="Esta é a primeira aula do curso"
          onNext={canGoNext ? goNext : undefined}
          nextDisabled={!canGoNext}
          nextLabel={nextLabel}
          nextPrimary={canGoNext}
        />
      </CoursePageShell>
    </>
  );
};

export default CourseLesson;
